<template>
  <div class="py-4 space-y-6">
    <!-- Title -->
    <div class="text-center mb-6">
      <h1 class="text-xl font-bold text-foreground mb-2">
        {{ $t('report.clients_new_returning_info.title') }}
      </h1>
      <p class="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">
        {{ $t('report.clients_new_returning_info.description') }}
      </p>
    </div>

    <!-- Segments -->
    <div class="space-y-4">
      <div class="flex items-center gap-2">
        <div class="w-1 h-3.5 rounded-full" style="background: #06C8FF"></div>
        <h2 class="text-sm font-bold uppercase tracking-wide text-foreground">
          {{ $t('report.clients_new_returning_info.how_to_read') }}
        </h2>
      </div>

      <!-- Mini illustration: stacked bars -->
      <div class="rounded-lg p-3 info-example-bg">
        <div class="flex items-end gap-3 justify-center h-20 px-6">
          <div class="flex flex-col items-center flex-1">
            <div class="w-full rounded-t overflow-hidden" style="height: 48px">
              <div class="w-full" style="height: 65%; background: #6366F1"></div>
              <div class="w-full" style="height: 35%; background: #10B981"></div>
            </div>
            <span class="text-[8px] font-semibold text-muted-foreground/60 mt-1">L</span>
          </div>
          <div class="flex flex-col items-center flex-1">
            <div class="w-full rounded-t overflow-hidden" style="height: 64px">
              <div class="w-full" style="height: 58%; background: #6366F1"></div>
              <div class="w-full" style="height: 42%; background: #10B981"></div>
            </div>
            <span class="text-[8px] font-semibold text-muted-foreground/60 mt-1">M</span>
          </div>
          <div class="flex flex-col items-center flex-1">
            <div class="w-full rounded-t overflow-hidden" style="height: 56px">
              <div class="w-full" style="height: 70%; background: #6366F1"></div>
              <div class="w-full" style="height: 30%; background: #10B981"></div>
            </div>
            <span class="text-[8px] font-semibold text-muted-foreground/60 mt-1">M</span>
          </div>
          <div class="flex flex-col items-center flex-1">
            <div class="w-full rounded-t overflow-hidden" style="height: 72px">
              <div class="w-full" style="height: 55%; background: #6366F1"></div>
              <div class="w-full" style="height: 45%; background: #10B981"></div>
            </div>
            <span class="text-[8px] font-semibold text-muted-foreground/60 mt-1">J</span>
          </div>
          <div class="flex flex-col items-center flex-1">
            <div class="w-full rounded-t overflow-hidden" style="height: 60px">
              <div class="w-full" style="height: 62%; background: #6366F1"></div>
              <div class="w-full" style="height: 38%; background: #10B981"></div>
            </div>
            <span class="text-[8px] font-semibold text-muted-foreground/60 mt-1">V</span>
          </div>
        </div>
        <div class="flex items-center justify-center gap-4 mt-2">
          <span class="flex items-center gap-1">
            <span class="w-1.5 h-1.5 rounded-full" style="background: #10B981"></span>
            <span class="text-[9px] font-semibold text-muted-foreground">{{ $t('report.clients_new') }}</span>
          </span>
          <span class="flex items-center gap-1">
            <span class="w-1.5 h-1.5 rounded-full" style="background: #6366F1"></span>
            <span class="text-[9px] font-semibold text-muted-foreground">{{ $t('report.clients_returning') }}</span>
          </span>
        </div>
      </div>

      <!-- Segment descriptions -->
      <div class="space-y-3">
        <div class="flex items-start gap-2.5">
          <span class="w-2.5 h-2.5 rounded-full shrink-0 mt-1" style="background: #10B981"></span>
          <div>
            <span class="text-xs font-semibold text-foreground">{{ $t('report.clients_new_returning_info.new_label') }}</span>
            <p class="text-xs text-muted-foreground leading-relaxed mt-0.5">
              {{ $t('report.clients_new_returning_info.new_desc') }}
            </p>
          </div>
        </div>

        <div class="flex items-start gap-2.5">
          <span class="w-2.5 h-2.5 rounded-full shrink-0 mt-1" style="background: #6366F1"></span>
          <div>
            <span class="text-xs font-semibold text-foreground">{{ $t('report.clients_new_returning_info.returning_label') }}</span>
            <p class="text-xs text-muted-foreground leading-relaxed mt-0.5">
              {{ $t('report.clients_new_returning_info.returning_desc') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Controls -->
    <div class="space-y-2.5">
      <div class="flex items-center gap-2">
        <div class="w-1 h-3.5 rounded-full" style="background: #06C8FF"></div>
        <h2 class="text-sm font-bold uppercase tracking-wide text-foreground">
          {{ $t('report.clients_new_returning_info.controls_section') }}
        </h2>
      </div>

      <div class="info-metric-item">
        <span class="info-metric-label">{{ $t('report.clients_new_returning_info.mode_label') }}</span>
        <p class="text-xs text-muted-foreground leading-relaxed mt-0.5">
          {{ $t('report.clients_new_returning_info.mode_desc') }}
        </p>
      </div>
      <div class="info-metric-item">
        <span class="info-metric-label">{{ $t('report.clients_new_returning_info.aggregation_label') }}</span>
        <p class="text-xs text-muted-foreground leading-relaxed mt-0.5">
          {{ $t('report.clients_new_returning_info.aggregation_desc') }}
        </p>
      </div>
    </div>

    <!-- Scope note -->
    <div class="info-note rounded-lg px-3 py-2.5">
      <p class="text-xs text-muted-foreground leading-relaxed">
        <span class="font-semibold text-foreground">{{ $t('report.clients_new_returning_info.anonymous_note_label') }}</span>
        {{ $t('report.clients_new_returning_info.anonymous_note') }}
      </p>
    </div>

  </div>
</template>

<script setup lang="ts">
// Content-only component — no props needed.
// Injected into a BottomSheet from CenterClientsReport.
</script>

<style scoped>
.info-note {
  background: rgba(245, 158, 11, 0.06);
  border: 1px solid rgba(245, 158, 11, 0.15);
}
:is(.dark *).info-note {
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.12);
}
.info-example-bg {
  background: rgba(107, 114, 128, 0.06);
  border: 1px solid rgba(107, 114, 128, 0.1);
}
:is(.dark *).info-example-bg {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.info-metric-item {
  padding-left: 0.75rem;
  border-left: 2px solid rgba(107, 114, 128, 0.15);
}
:is(.dark *).info-metric-item {
  border-left-color: rgba(255, 255, 255, 0.1);
}
.info-metric-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--foreground);
}
</style>
